import { useEffect, useState } from "react";
import { api, idr } from "../../lib/api";
import { toast } from "sonner";
import { Users, Plus, DollarSign, PieChart, Landmark } from "lucide-react";
import InputRupiah from "../../components/InputRupiah";
import { useAuth } from "../../context/AuthContext";
import { DatePicker, MonthPicker } from "../../components/ui/date-picker";
import LoadingState from "../../components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatBulan } from "../../lib/tanggal";
import { useSortState, sortRows, SortTh } from "../../components/ui/sort";

// Backend (routers/finance/dividen.py) menyimpan status deklarasi
// "declared" | "partial" | "paid_all" dan status per pemegang saham
// "unpaid" | "partial" | "paid". Dipetakan di sini supaya badge tidak lagi
// membandingkan dengan nilai karangan ("lunas") yang tidak pernah cocok.
const STATUS_DIV = {
  declared: { label: "Dideklarasikan", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  partial: { label: "Sebagian Dibayar", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  paid_all: { label: "Lunas", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};
const STATUS_SH = { unpaid: "belum bayar", partial: "sebagian dibayar", paid: "lunas" };

const TABS = [
  { key: "deklarasi", label: "Deklarasi Dividen", icon: DollarSign },
  { key: "pemegang", label: "Pemegang Saham", icon: Users },
  { key: "captable", label: "Cap Table", icon: PieChart },
];

export default function FinanceDividen() {
  const { can } = useAuth();
  const [tab, setTab] = useState("deklarasi");
  const [showForm, setShowForm] = useState(false);
  // Bump untuk remount DeklarasiTab (memicu load() ulang) setelah deklarasi
  // baru disimpan. Tombol "Deklarasi Baru" & FormDividen dirender di sini
  // (parent), bukan di dalam DeklarasiTab, jadi pola ShareholderSection
  // (onClose memanggil load() langsung) tidak bisa ditiru tanpa memindahkan
  // tombol ke dalam tab — remount via key adalah perubahan paling kecil yang
  // tetap benar untuk struktur ini.
  const [reloadKey, setReloadKey] = useState(0);

  const visibleTabs = TABS.filter(t => t.key !== "pemegang" || can("finance_pengaturan", "view"));

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="finance-dividen">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Pemegang Saham</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Dividen</h1>
          <p className="text-sm text-slate-600 mt-1">Deklarasi & pembayaran dividen per pemegang saham.</p>
        </div>
        {tab === "deklarasi" && can("finance_dividen", "create") && (
          <button onClick={() => setShowForm(true)} data-testid="div-new" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Deklarasi Baru
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-1 inline-flex flex-wrap gap-1">
        {visibleTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`div-tab-${t.key}`}
              className={`px-4 h-9 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition ${tab === t.key ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:bg-slate-50"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "deklarasi" && <DeklarasiTab key={reloadKey} />}
      {tab === "pemegang" && can("finance_pengaturan", "view") && <ShareholderSection can={can} />}
      {tab === "captable" && <CapTableSection />}

      {showForm && <FormDividen onClose={() => { setShowForm(false); setReloadKey(k => k + 1); }} />}
    </div>
  );
}

/* ================ TAB: Deklarasi Dividen (daftar lama) ==================== */
function DeklarasiTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/dividen");
      setItems(r.data.items || []);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={2} data-testid="loading-dividen">
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
          <Users className="w-12 h-12 mx-auto text-slate-400 mb-3" />
          <p className="font-display font-bold text-lg text-slate-900">Belum ada dividen</p>
          <p className="text-sm text-slate-500 mt-1">Deklarasi pertama akan tampil di sini.</p>
        </div>
      ) : items.map(d => {
        const meta = STATUS_DIV[d.status] || STATUS_DIV.declared;
        return (
          <div key={d.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{d.code} · {formatBulan(d.periode_bagi_hasil)}</p>
                <p className="font-display font-bold text-xl text-slate-900 mt-1">{idr(d.total_amount)}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">Dibayar {idr(d.total_paid || 0)} dari {idr(d.total_amount)}</p>
              </div>
              <div className={`px-2 py-1 rounded-full text-[10px] font-semibold border ${meta.cls}`}>{meta.label}</div>
            </div>
            <div className="border-t border-slate-100 pt-3 space-y-2">
              {(d.shareholders || []).map((s, i) => (
                <div key={s.id || i} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="font-medium text-slate-800">{s.nama}</p>
                    <p className="text-[10px] text-slate-500">{s.share_pct}% · Status: {STATUS_SH[s.status] || "belum bayar"}</p>
                  </div>
                  <p className="font-mono font-semibold text-slate-900">{idr(s.amount)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
    </LoadingState>
  );
}

/* ================ TAB: Pemegang Saham (Master) — Finance #9/#14 ==================== */
function ShareholderSection({ can }) {
  const [items, setItems] = useState([]);
  const [totalPersen, setTotalPersen] = useState(0);
  const [warning, setWarning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null); // null=closed, {}=baru, {...}=edit

  const canWrite = can("finance_pengaturan", "update");

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.get("/finance/dividen/shareholders");
      setItems(r.data.items || []);
      setTotalPersen(r.data.total_persen || 0);
      setWarning(r.data.warning || null);
    } catch (e) { setLoadError(e); toast.error("Gagal memuat pemegang saham"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const nonaktifkan = async (sh) => {
    if (!window.confirm(`Nonaktifkan pemegang saham '${sh.nama}'?`)) return;
    try {
      const r = await api.delete(`/finance/dividen/shareholders/${sh.id}`);
      toast.success(r.data.message || "Pemegang saham dinonaktifkan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menonaktifkan"); }
  };

  // Backend sudah urut persentase desc (master kecil, tidak dipotong query
  // apa pun) — urutan bawaan itu tetap dipertahankan sampai header diklik.
  const { sortKey, sortDir, toggle } = useSortState(null);
  const sortedItems = sortRows(items, sortKey, sortDir, {
    nama: (sh) => sh.nama || "",
    persentase: (sh) => sh.persentase || 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-col md:flex-row gap-3">
        <div>
          <p className="font-display font-bold text-lg text-slate-900">Pemegang Saham (Master)</p>
          <p className="text-sm text-slate-500 mt-0.5">Referensi permanen dipakai form Deklarasi Dividen supaya tidak ketik ulang nama & persentase.</p>
        </div>
        {canWrite && (
          <button onClick={() => setEditing({})} data-testid="sh-tambah" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium">
            <Plus className="w-4 h-4" /> Tambah Pemegang Saham
          </button>
        )}
      </div>

      {warning && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">{warning}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <SortTh label="Nama" sortKeyName="nama" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="sh-sort-nama" />
                <SortTh label="Persentase" sortKeyName="persentase" sortKey={sortKey} sortDir={sortDir} onSort={toggle} align="right" testid="sh-sort-persentase" />
                <th className="px-4 py-2.5 text-left">Keterangan</th>
                {canWrite && <th className="px-4 py-2.5 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              <LoadingState loading={loading} error={loadError} onRetry={load} kosong={items.length === 0} pesanKosong="Belum ada pemegang saham. Tambahkan dulu supaya bisa dipakai di form Deklarasi." varian="tabel" kolom={canWrite ? 4 : 3} data-testid="loading-shareholders">
              {sortedItems.map(sh => (
                <tr key={sh.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-3 font-medium text-slate-900">{sh.nama}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">{sh.persentase}%</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{sh.keterangan || "-"}</td>
                  {canWrite && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(sh)} data-testid={`sh-edit-${sh.id}`} className="text-xs font-medium text-slate-700 hover:underline mr-3">Edit</button>
                      <button onClick={() => nonaktifkan(sh)} data-testid={`sh-nonaktif-${sh.id}`} className="text-xs font-medium text-rose-700 hover:underline">Nonaktifkan</button>
                    </td>
                  )}
                </tr>
              ))}
              </LoadingState>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-slate-100 text-xs text-slate-500">
          Total persentase pemegang saham aktif: <span className="font-semibold text-slate-700">{totalPersen}%</span>
        </div>
      </div>

      {editing && <FormShareholder initial={editing.id ? editing : null} onClose={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function FormShareholder({ initial, onClose }) {
  const [form, setForm] = useState({
    nama: initial?.nama || "",
    persentase: initial?.persentase ?? "",
    keterangan: initial?.keterangan || "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.nama.trim()) return toast.error("Nama pemegang saham wajib diisi");
    const persen = Number(form.persentase);
    if (!form.persentase || persen <= 0 || persen > 100) return toast.error("Persentase harus lebih dari 0 dan maksimal 100");
    setBusy(true);
    try {
      if (initial) {
        const r = await api.put(`/finance/dividen/shareholders/${initial.id}`, {
          nama: form.nama, persentase: persen, keterangan: form.keterangan,
        });
        toast.success(r.data.message || "Pemegang saham diperbarui");
      } else {
        const r = await api.post("/finance/dividen/shareholders", {
          nama: form.nama, persentase: persen, keterangan: form.keterangan,
        });
        toast.success(r.data.message || "Pemegang saham ditambahkan");
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-slate-600" /> {initial ? "Edit" : "Tambah"} Pemegang Saham</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Nama</label>
            <input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} data-testid="sh-nama" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Persentase (%)</label>
            <input type="number" min="0" max="100" step="0.01" value={form.persentase} onChange={e => setForm({ ...form, persentase: e.target.value })} data-testid="sh-persen" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Keterangan</label>
            <input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} placeholder="opsional" data-testid="sh-keterangan" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={busy} data-testid="sh-submit" className="flex-1 h-10 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Simpan"}</button>
        </div>
      </div>
    </div>
  );
}

/* ================ TAB: Cap Table ==================== */
function CapTableSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    api.get("/finance/dividen/cap-table")
      .then(r => setData(r.data))
      .catch((e) => { setLoadError(e); toast.error("Gagal memuat cap table"); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <LoadingState loading={loading} error={loadError} onRetry={load} varian="kartu" jumlahKartu={2} data-testid="loading-captable">
    {data && <div data-testid="captable" className="space-y-4">
      {data.peringatan_persen && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">{data.peringatan_persen}</div>
      )}

      {data.shareholders.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
          <PieChart className="w-12 h-12 mx-auto text-slate-400 mb-3" />
          <p className="font-display font-bold text-lg text-slate-900">Belum ada pemegang saham aktif</p>
          <p className="text-sm text-slate-500 mt-1">Tambahkan di tab Pemegang Saham dulu.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.shareholders.map(s => (
            <div key={s.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div>
                <p className="font-display font-bold text-base text-slate-900">{s.nama}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.persentase}% kepemilikan{s.keterangan ? ` · ${s.keterangan}` : ""}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center border-t border-slate-100 pt-3">
                <div>
                  <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Dideklarasikan</p>
                  <p className="font-mono font-semibold text-slate-900 text-xs mt-1">{idr(s.total_dividen_dideklarasikan)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Dibayar</p>
                  <p className="font-mono font-semibold text-emerald-700 text-xs mt-1">{idr(s.total_dividen_dibayar)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Sisa Utang</p>
                  <p className="font-mono font-semibold text-rose-700 text-xs mt-1">{idr(s.total_dividen_belum_dibayar)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <Landmark className="w-4 h-4 text-slate-500" />
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Modal Disetor (Agregat Seluruh Pemegang Saham)</p>
        </div>
        <p className="font-display text-xl font-bold text-slate-900 mt-1">{idr(data.modal_disetor.total)}</p>
        <p className="text-xs text-slate-500 mt-2 leading-relaxed">{data.modal_disetor.catatan}</p>
      </div>
    </div>}
    </LoadingState>
  );
}

/* ================ Form Deklarasi Dividen ==================== */
function FormDividen({ onClose }) {
  // Nama field WAJIB persis seperti DividenReq di backend/routers/finance/_shared.py
  // (tanggal_deklarasi, periode_bagi_hasil, total_amount, shareholders[].share_pct).
  // Versi lama mengirim periode/total_nilai/persen sehingga selalu ditolak 422.
  const [form, setForm] = useState({
    tanggal_deklarasi: hariIniISO(),
    periode_bagi_hasil: bulanIniISO(),
    total_amount: "",
    keterangan: "",
    shareholders: [{ nama: "", share_pct: 100 }],
  });
  const [master, setMaster] = useState([]);
  const [masterLoaded, setMasterLoaded] = useState(false);
  // Toggle "Ambil dari master" — default ON begitu master aktif termuat & tidak kosong
  // (Finance #9/#14). User tetap bisa mematikannya untuk mode manual lama.
  const [dariMaster, setDariMaster] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await api.get("/finance/dividen/shareholders");
        if (batal) return;
        const items = r.data.items || [];
        setMaster(items);
        setDariMaster(items.length > 0);
      } catch { /* form tetap bisa dipakai manual walau master gagal dimuat */ }
      finally { if (!batal) setMasterLoaded(true); }
    })();
    return () => { batal = true; };
  }, []);

  const totalPersenManual = form.shareholders.reduce((s, x) => s + Number(x.share_pct || 0), 0);
  const totalPersenMaster = master.reduce((s, x) => s + Number(x.persentase || 0), 0);
  const totalPersen = dariMaster ? totalPersenMaster : totalPersenManual;
  // Toleransi disamakan dengan backend (0,01). Kalau di sini lebih longgar,
  // form terlihat valid tapi request tetap ditolak 400 di server.
  const persenValid = Math.abs(totalPersen - 100) <= 0.01;

  const submit = async () => {
    if (dariMaster) {
      if (master.length === 0) return toast.error("Master pemegang saham aktif masih kosong. Isi dulu di tab Pemegang Saham 😊");
    } else {
      if (!persenValid) return toast.error("Total persen kepemilikan harus tepat 100%");
      if (form.shareholders.some(s => !s.nama.trim())) return toast.error("Nama pemegang saham belum lengkap");
    }
    setBusy(true);
    try {
      const payload = {
        tanggal_deklarasi: form.tanggal_deklarasi,
        periode_bagi_hasil: form.periode_bagi_hasil,
        total_amount: Number(form.total_amount) || 0,
        keterangan: form.keterangan,
        dari_master: dariMaster,
        shareholders: dariMaster
          ? []
          : form.shareholders.map(s => ({ nama: s.nama, share_pct: Number(s.share_pct) || 0 })),
      };
      const r = await api.post("/finance/dividen", payload);
      toast.success(r.data.message || "Dividen dideklarasikan");
      if (r.data.warning) toast.warning(r.data.warning);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const disableSubmit = busy || !form.total_amount || (dariMaster ? master.length === 0 : !persenValid);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-slate-600" /> Deklarasi Dividen</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Tanggal Deklarasi</label>
              <DatePicker value={form.tanggal_deklarasi} onChange={v => setForm({ ...form, tanggal_deklarasi: v })} data-testid="div-tanggal" />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Periode Bagi Hasil</label>
              <MonthPicker value={form.periode_bagi_hasil} onChange={v => setForm({ ...form, periode_bagi_hasil: v })} data-testid="div-periode" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Total Dividen</label>
            <InputRupiah value={form.total_amount} onChange={n => setForm({ ...form, total_amount: n })} className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" testid="div-total" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Keterangan</label>
            <input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} placeholder="opsional" className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm" />
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-800 pt-1">
            <input type="checkbox" checked={dariMaster} onChange={e => setDariMaster(e.target.checked)} disabled={!masterLoaded || master.length === 0} data-testid="dv-dari-master" className="w-4 h-4 accent-emerald-600" />
            Ambil dari master pemegang saham
          </label>
          {masterLoaded && master.length === 0 && (
            <p className="text-xs text-amber-600">Master pemegang saham masih kosong — isi dulu di tab Pemegang Saham, atau isi manual di bawah.</p>
          )}

          {dariMaster ? (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Pratinjau Pembagian (dari master, total {totalPersenMaster.toFixed(2)}%)</label>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
                {master.map(m => (
                  <div key={m.id} className="flex items-center justify-between text-sm">
                    <p className="text-slate-800">{m.nama} <span className="text-slate-400">({m.persentase}%)</span></p>
                    <p className="font-mono font-semibold text-slate-900">{form.total_amount ? idr(Number(form.total_amount) * m.persentase / 100) : "-"}</p>
                  </div>
                ))}
              </div>
              {!persenValid && (
                <p className="text-xs text-rose-600 mt-1">Total persentase master {totalPersenMaster.toFixed(2)}% (bukan 100%) — perbaiki dulu di tab Pemegang Saham sebelum deklarasi.</p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={`text-[11px] font-semibold uppercase tracking-wider ${persenValid ? "text-slate-500" : "text-rose-600"}`}>Pemegang Saham (total {totalPersenManual.toFixed(2)}%)</label>
                <button onClick={() => setForm({ ...form, shareholders: [...form.shareholders, { nama: "", share_pct: 0 }] })} className="text-xs text-emerald-700 font-medium">+ Tambah</button>
              </div>
              <div className="space-y-2">
                {form.shareholders.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <input placeholder="Nama" value={s.nama} onChange={e => { const arr = [...form.shareholders]; arr[i] = { ...arr[i], nama: e.target.value }; setForm({ ...form, shareholders: arr }); }} className="flex-1 h-9 px-2 border border-slate-200 rounded text-sm" />
                    <input type="number" placeholder="%" value={s.share_pct} onChange={e => { const arr = [...form.shareholders]; arr[i] = { ...arr[i], share_pct: e.target.value }; setForm({ ...form, shareholders: arr }); }} className="w-20 h-9 px-2 border border-slate-200 rounded text-sm" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm">Batal</button>
          <button onClick={submit} disabled={disableSubmit} data-testid="div-submit" className="flex-1 h-10 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium disabled:opacity-50">{busy ? "..." : "Deklarasi"}</button>
        </div>
      </div>
    </div>
  );
}
